import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { AtSign, Send } from "lucide-react";
import { toast } from "sonner";

import { getLanguage } from "@/utils/languageUtils";
import { leadUILabels, commonMiscLabels, commonButtonLabels } from "@/i18n";

export interface ColleagueForMention {
  id: string;
  name: string;
}

interface DealerPortalCommentFormProps {
  onSubmit: (text: string, isPublic: boolean, mentions: string[]) => void;
  isSubmitting?: boolean;
  /** Colleagues from dealer portal (GET /dealer-portal/colleagues) for @mentions */
  colleagues: ColleagueForMention[];
  isLoadingColleagues?: boolean;
}

/**
 * Comment form for dealer portal – uses dealer portal colleagues for @mentions.
 * Does not call any admin API (no admins-for-mentions, no online-users/count).
 */
export function DealerPortalCommentForm({
  onSubmit,
  isSubmitting = false,
  colleagues,
  isLoadingColleagues = false,
}: DealerPortalCommentFormProps) {
  const [newComment, setNewComment] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentions, setMentions] = useState<string[]>([]);

  const uiLabels = leadUILabels(getLanguage());
  const commonLabels = commonMiscLabels(getLanguage());
  const commonButtons = commonButtonLabels(getLanguage());

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    onSubmit(newComment, isPublic, mentions);

    if (mentions.length > 0) {
      const mentionedNames = mentions
        .map((actorId) => colleagues.find((c) => c.id === actorId)?.name)
        .filter(Boolean)
        .join(", ");
      toast.success(uiLabels.notificationsSent, {
        description: uiLabels.notificationsSentDescription.replace(
          "{names}",
          mentionedNames,
        ),
      });
    }

    setNewComment("");
    setIsPublic(false);
    setMentions([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "@") {
      setShowMentions(true);
    }
  };

  const insertMention = (colleague: ColleagueForMention) => {
    setNewComment((prev) => `${prev}@${colleague.name} `);
    setMentions((prev) =>
      prev.includes(colleague.id) ? prev : [...prev, colleague.id],
    );
    setShowMentions(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="relative">
        <Textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={uiLabels.commentPlaceholder}
          className="min-h-[80px] resize-none"
        />
        {showMentions && (
          <div className="absolute bottom-full left-0 bg-card border rounded-md shadow-lg p-2 space-y-1 w-64 z-10 max-h-64 overflow-y-auto">
            {isLoadingColleagues ? (
              <div className="px-2 py-1 text-sm text-muted-foreground">
                {uiLabels.loadingColleagues}
              </div>
            ) : colleagues.length === 0 ? (
              <div className="px-2 py-1 text-sm text-muted-foreground">
                {uiLabels.noColleaguesAvailable}
              </div>
            ) : (
              colleagues.map((colleague) => (
                <button
                  key={colleague.id}
                  type="button"
                  onClick={() => insertMention(colleague)}
                  className="w-full hover:bg-accent/20 text-left px-2 py-1 rounded"
                >
                  {colleague.name}
                </button>
              ))
            )}
          </div>
        )}
      </div>
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowMentions(!showMentions)}
            className="hover:bg-accent/20"
            disabled={isLoadingColleagues}
          >
            <AtSign />
            <p className="">{commonLabels.notifyColleagues}</p>
            {mentions.length > 0 && (
              <span className="ml-2 bg-primary/20 text-primary rounded-full px-2 py-0.5 text-xs">
                {mentions.length}
              </span>
            )}
          </Button>
        </div>
        <Button
          type="submit"
          size="sm"
          disabled={!newComment.trim() || isSubmitting}
        >
          <Send />
          {isSubmitting ? commonButtons.sending : commonButtons.send}
        </Button>
      </div>
    </form>
  );
}
